import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineClipboardDocumentCheck,
  HiOutlineDocumentMagnifyingGlass,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import authService from '../../../services/authService';
import machineService from '../../../services/machineService';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const PmHistoryPage = () => {
  // Get cached data if available
  const getCachedData = () => {
    try {
      const cachedHistory = sessionStorage.getItem('pm_history_data');
      const cachedSchedules = sessionStorage.getItem('pm_schedules_data');
      return {
        history: cachedHistory ? JSON.parse(cachedHistory) : [],
        schedules: cachedSchedules ? JSON.parse(cachedSchedules) : [],
        hasAll: !!(cachedHistory && cachedSchedules),
      };
    } catch {
      return { history: [], schedules: [], hasAll: false };
    }
  };

  // Initialize with cached data if available
  const cachedInitialData = getCachedData();
  const [history, setHistory] = useState(cachedInitialData.history);
  const [schedules, setSchedules] = useState(cachedInitialData.schedules);
  const [plans, setPlans] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(!cachedInitialData.hasAll);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [viewTasks, setViewTasks] = useState([]);
  const [viewTaskResults, setViewTaskResults] = useState({});
  const [viewLoading, setViewLoading] = useState(false);
  const [formData, setFormData] = useState({
    pmScheduleId: '',
    workOrderId: '',
    plannedDate: '',
    executedDate: '',
    executedBy: '',
    completionStatus: '',
    checklistResultsJson: '',
    findings: '',
    attachmentsJson: '',
    elapsedMinutes: '',
    costLabor: '',
    costParts: '',
    totalCost: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const tenantId = authService.getTenantId();
    if (tenantId) {
      loadData(!cachedInitialData.hasAll);
    }
  }, []);
  const loadData = async (showLoading = true) => {
    const tenantId = authService.getTenantId();
    if (!tenantId) return;

    try {
      if (showLoading) setLoading(true);
      setError('');
      
      const [historyData, schedulesData, plansData, machinesData] = await Promise.all([
        masterDataService.getPmHistory(tenantId).catch(() => []),
        masterDataService.getPMSchedules(tenantId).catch(() => []),
        masterDataService.getMaintenancePlans(tenantId).catch(() => []),
        machineService.getMachines(tenantId).catch(() => []),
      ]);
      
      setHistory(Array.isArray(historyData) ? historyData : []);
      setSchedules(Array.isArray(schedulesData) ? schedulesData : []);
      setPlans(Array.isArray(plansData) ? plansData : []);
      setMachines(Array.isArray(machinesData) ? machinesData : []);
      
      sessionStorage.setItem('pm_history_data', JSON.stringify(historyData));
      sessionStorage.setItem('pm_schedules_data', JSON.stringify(schedulesData));
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load data. Please try again.';
      setError(errorMessage);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const getPlanName = (scheduleId) => {
    if (!scheduleId) return '-';
    const schedule = schedules.find((s) => s.pmScheduleId === scheduleId);
    if (!schedule) return '-';
    const plan = plans.find(p => p.maintenancePlanId === schedule.maintenancePlanId);
    return plan ? `${plan.planCode} - ${plan.planName}` : `Plan ${schedule.maintenancePlanId}`;
  };

  const getMachineName = (scheduleId) => {
    if (!scheduleId) return '-';
    const schedule = schedules.find((s) => s.pmScheduleId === scheduleId);
    if (!schedule) return '-';
    const machine = machines.find(m => m.machineId === schedule.assetId);
    return machine ? `${machine.machineCode} - ${machine.machineName}` : `Machine ${schedule.assetId}`;
  };

  const filteredHistory = history.filter((item) => {
    const schedule = schedules.find((s) => s.pmScheduleId === item.pmScheduleId);
    const searchString = `${item.pmScheduleId} ${item.completionStatus} ${schedule?.assetId || ''} ${schedule?.maintenancePlanId || ''}`.toLowerCase();
    return searchString.includes(searchTerm.toLowerCase());
  });

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      pmScheduleId: '',
      workOrderId: '',
      plannedDate: '',
      executedDate: '',
      executedBy: '',
      completionStatus: '',
      checklistResultsJson: '',
      findings: '',
      attachmentsJson: '',
      elapsedMinutes: '',
      costLabor: '',
      costParts: '',
      totalCost: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toISOString().split('T')[0];
    };
    setEditingItem(item);
    setFormData({
      pmScheduleId: item.pmScheduleId?.toString() || '',
      workOrderId: item.workOrderId?.toString() || '',
      plannedDate: formatDate(item.plannedDate),
      executedDate: formatDate(item.executedDate),
      executedBy: item.executedBy || '',
      completionStatus: item.completionStatus || '',
      checklistResultsJson: typeof item.checklistResultsJson === 'string'
        ? item.checklistResultsJson
        : JSON.stringify(item.checklistResultsJson || []),
      findings: item.findings || '',
      attachmentsJson: typeof item.attachmentsJson === 'string'
        ? item.attachmentsJson
        : JSON.stringify(item.attachmentsJson || []),
      elapsedMinutes: item.elapsedMinutes?.toString() || '',
      costLabor: item.costLabor?.toString() || '',
      costParts: item.costParts?.toString() || '',
      totalCost: item.totalCost?.toString() || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.pmScheduleId) {
      errors.pmScheduleId = 'Schedule is required';
    }
    if (formData.checklistResultsJson) {
      try {
        JSON.parse(formData.checklistResultsJson);
      } catch {
        errors.checklistResultsJson = 'Invalid JSON format';
      }
    }
    if (formData.attachmentsJson) {
      try {
        JSON.parse(formData.attachmentsJson);
      } catch {
        errors.attachmentsJson = 'Invalid JSON format';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const tenantId = authService.getTenantId();
    if (!tenantId) {
      setError('Please login to save PM history');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const payload = {
        tenantId,
        pmScheduleId: parseInt(formData.pmScheduleId),
        workOrderId: formData.workOrderId ? parseInt(formData.workOrderId) : null,
        plannedDate: formData.plannedDate ? new Date(formData.plannedDate) : null,
        executedDate: formData.executedDate ? new Date(formData.executedDate) : null,
        executedBy: formData.executedBy.trim() || null,
        completionStatus: formData.completionStatus.trim() || null,
        checklistResultsJson: formData.checklistResultsJson.trim() || null,
        findings: formData.findings.trim() || null,
        attachmentsJson: formData.attachmentsJson.trim() || null,
        elapsedMinutes: formData.elapsedMinutes ? parseFloat(formData.elapsedMinutes) : null,
        costLabor: formData.costLabor ? parseFloat(formData.costLabor) : null,
        costParts: formData.costParts ? parseFloat(formData.costParts) : null,
        totalCost: formData.totalCost ? parseFloat(formData.totalCost) : null,
      };

      if (editingItem) {
        payload.pmHistoryId = editingItem.pmHistoryId;
        await masterDataService.updatePmHistory(editingItem.pmHistoryId, payload);
      } else {
        await masterDataService.createPmHistory(payload);
      }

      await loadData();
      setShowModal(false);
      setEditingItem(null);
      setFormData({
        pmScheduleId: '',
        workOrderId: '',
        plannedDate: '',
        executedDate: '',
        executedBy: '',
        completionStatus: '',
        checklistResultsJson: '',
        findings: '',
        attachmentsJson: '',
        elapsedMinutes: '',
        costLabor: '',
        costParts: '',
        totalCost: '',
      });
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        'Failed to save PM history. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      pmScheduleId: '',
      workOrderId: '',
      plannedDate: '',
      executedDate: '',
      executedBy: '',
      completionStatus: '',
      checklistResultsJson: '',
      findings: '',
      attachmentsJson: '',
      elapsedMinutes: '',
      costLabor: '',
      costParts: '',
      totalCost: '',
    });
    setFormErrors({});
  };

  const getScheduleLabel = (id) => {
    if (!id) return '-';
    return `Schedule ${id}`;
  };

  const parseChecklistResults = (jsonStr) => {
    if (!jsonStr || typeof jsonStr !== 'string') return [];
    try {
      const parsed = JSON.parse(jsonStr);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const openView = async (item) => {
    try {
      setError('');
      setViewLoading(true);
      setViewItem(item);
      setViewTasks([]);
      setViewTaskResults({});

      const tenantId = authService.getTenantId();
      if (!tenantId) {
        setError('Please login to view checklist details');
        setViewLoading(false);
        return;
      }

      const schedule = schedules.find((s) => s.pmScheduleId === item.pmScheduleId);
      if (!schedule) {
        setShowViewModal(true);
        return;
      }

      const tasks = await masterDataService.getMaintenancePlanTasks(tenantId, schedule.maintenancePlanId);
      const resultsArr = parseChecklistResults(item.checklistResultsJson);
      const resultsMap = (resultsArr || []).reduce((acc, r) => {
        const taskId = r?.taskId ?? r?.TaskId;
        const result = r?.result ?? r?.Result ?? '';
        if (taskId) acc[taskId] = result;
        return acc;
      }, {});

      setViewTasks(tasks || []);
      setViewTaskResults(resultsMap);
      setShowViewModal(true);
    } catch (err) {
      setError('Failed to load checklist details. Please try again.');
      console.error('Error viewing checklist:', err);
    } finally {
      setViewLoading(false);
    }
  };

  const closeView = () => {
    setShowViewModal(false);
    setViewItem(null);
    setViewTasks([]);
    setViewTaskResults({});
    setViewLoading(false);
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading PM history..." />
      </div>
    );
  }

  return (
    <div className="pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h2>PM History</h2>
          <p>Manage PM history records for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add History
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search history..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredHistory.length} {filteredHistory.length === 1 ? 'record' : 'records'}
          </div>
        </div>

        {filteredHistory.length === 0 ? (
          <div className="empty-state">
            <HiOutlineClipboardDocumentCheck className="empty-icon" />
            <h3>No history found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first PM history record'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add History
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Plan</th>
                <th>Asset</th>
                <th>Work Order</th>
                <th>Status</th>
                <th>Executed Date</th>
                <th>Total Cost</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.map((item) => (
                <tr key={item.pmHistoryId}>
                  <td>{item.pmHistoryId}</td>
                  <td>{getPlanName(item.pmScheduleId)}</td>
                  <td>{getMachineName(item.pmScheduleId)}</td>
                  <td>{item.workOrderId ?? '-'}</td>
                  <td>{item.completionStatus || '-'}</td>
                  <td>{item.executedDate ? new Date(item.executedDate).toLocaleDateString() : '-'}</td>
                  <td>{item.totalCost ?? '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => openView(item)}
                        title="View Checklist"
                      >
                        <HiOutlineDocumentMagnifyingGlass />
                      </button>
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(item)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingItem ? 'Edit PM History' : 'Add New PM History'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              form="pm-history-form"
              className="btn-primary" 
              disabled={submitting}
            >
              {submitting
                ? editingItem
                  ? 'Updating...'
                  : 'Creating...'
                : editingItem
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="pm-history-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="pmScheduleId">
                Schedule <span className="required">*</span>
              </label>
              <select
                id="pmScheduleId"
                value={formData.pmScheduleId}
                onChange={(e) => setFormData({ ...formData, pmScheduleId: e.target.value })}
                className={formErrors.pmScheduleId ? 'error' : ''}
              >
                <option value="">Select Schedule</option>
                {schedules.map((s) => (
                  <option key={s.pmScheduleId} value={s.pmScheduleId.toString()}>
                    Schedule {s.pmScheduleId}
                  </option>
                ))}
              </select>
              {formErrors.pmScheduleId && (
                <span className="field-error">{formErrors.pmScheduleId}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="workOrderId">Work Order ID</label>
              <input
                type="number"
                id="workOrderId"
                value={formData.workOrderId}
                onChange={(e) => setFormData({ ...formData, workOrderId: e.target.value })}
                placeholder="Enter work order ID (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="plannedDate">Planned Date</label>
              <input
                type="date"
                id="plannedDate"
                value={formData.plannedDate}
                onChange={(e) => setFormData({ ...formData, plannedDate: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label htmlFor="executedDate">Executed Date</label>
              <input
                type="date"
                id="executedDate"
                value={formData.executedDate}
                onChange={(e) => setFormData({ ...formData, executedDate: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label htmlFor="executedBy">Executed By</label>
              <input
                type="text"
                id="executedBy"
                value={formData.executedBy}
                onChange={(e) => setFormData({ ...formData, executedBy: e.target.value })}
                placeholder="Enter executor (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="completionStatus">Completion Status</label>
              <input
                type="text"
                id="completionStatus"
                value={formData.completionStatus}
                onChange={(e) =>
                  setFormData({ ...formData, completionStatus: e.target.value })
                }
                placeholder="Enter completion status (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="checklistResultsJson">Checklist Results (JSON)</label>
              <textarea
                id="checklistResultsJson"
                value={formData.checklistResultsJson}
                onChange={(e) =>
                  setFormData({ ...formData, checklistResultsJson: e.target.value })
                }
                placeholder='e.g., [{"item": "Check belt", "status": "OK"}] (optional)'
                rows="3"
                className={formErrors.checklistResultsJson ? 'error' : ''}
              />
              {formErrors.checklistResultsJson && (
                <span className="field-error">{formErrors.checklistResultsJson}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="findings">Findings</label>
              <textarea
                id="findings"
                value={formData.findings}
                onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
                placeholder="Enter findings (optional)"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="attachmentsJson">Attachments (JSON)</label>
              <textarea
                id="attachmentsJson"
                value={formData.attachmentsJson}
                onChange={(e) =>
                  setFormData({ ...formData, attachmentsJson: e.target.value })
                }
                placeholder='e.g., [{"name": "photo.jpg", "url": "..."}] (optional)'
                rows="3"
                className={formErrors.attachmentsJson ? 'error' : ''}
              />
              {formErrors.attachmentsJson && (
                <span className="field-error">{formErrors.attachmentsJson}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="elapsedMinutes">Elapsed Minutes</label>
              <input
                type="number"
                id="elapsedMinutes"
                value={formData.elapsedMinutes}
                onChange={(e) => setFormData({ ...formData, elapsedMinutes: e.target.value })}
                placeholder="Enter elapsed minutes (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="costLabor">Cost Labor</label>
              <input
                type="number"
                id="costLabor"
                value={formData.costLabor}
                onChange={(e) => setFormData({ ...formData, costLabor: e.target.value })}
                placeholder="Enter labor cost (optional)"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="costParts">Cost Parts</label>
              <input
                type="number"
                id="costParts"
                value={formData.costParts}
                onChange={(e) => setFormData({ ...formData, costParts: e.target.value })}
                placeholder="Enter parts cost (optional)"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="totalCost">Total Cost</label>
              <input
                type="number"
                id="totalCost"
                value={formData.totalCost}
                onChange={(e) => setFormData({ ...formData, totalCost: e.target.value })}
                placeholder="Enter total cost (optional)"
                step="0.01"
              />
            </div>

          </form>
        </div>
      </SharedModal>

      {/* View Checklist Modal */}
      <SharedModal
        isOpen={showViewModal && !!viewItem}
        onClose={closeView}
        title="PM Completion Details"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={closeView}>
              Close
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          {viewLoading ? (
            <PageLoading message="Loading checklist..." variant="compact" />
          ) : viewItem ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                <div><strong>Schedule:</strong> {getScheduleLabel(viewItem.pmScheduleId)}</div>
                <div><strong>Status:</strong> {viewItem.completionStatus || '-'}</div>
                <div><strong>Planned Date:</strong> {viewItem.plannedDate ? new Date(viewItem.plannedDate).toLocaleDateString() : '-'}</div>
                <div><strong>Executed Date:</strong> {viewItem.executedDate ? new Date(viewItem.executedDate).toLocaleDateString() : '-'}</div>
                <div><strong>Executed By:</strong> {viewItem.executedBy || '-'}</div>
                <div><strong>Elapsed (min):</strong> {viewItem.elapsedMinutes ?? '-'}</div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <strong>Findings:</strong>
                <div style={{ marginTop: '0.35rem', color: 'var(--text-primary)' }}>
                  {viewItem.findings || '-'}
                </div>
              </div>

              <div>
                <strong>Checklist Results:</strong>
                {viewTasks.length === 0 ? (
                  <div style={{ marginTop: '0.35rem', color: 'var(--text-tertiary)' }}>
                    No tasks found for the plan (or unable to load tasks). Raw data:
                    <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{viewItem.checklistResultsJson || '-'}</pre>
                  </div>
                ) : (
                  <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {viewTasks.map((t) => (
                      <div
                        key={t.taskId}
                        style={{
                          border: '1px solid var(--border-primary)',
                          borderRadius: 10,
                          padding: '0.75rem',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                          <div style={{ fontWeight: 600 }}>{t.taskDescription}</div>
                          {t.isMandatory && <span className="required">*</span>}
                        </div>
                        <div style={{ marginTop: '0.35rem', color: 'var(--text-primary)' }}>
                          {String(viewTaskResults?.[t.taskId] ?? '').trim() || '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </SharedModal>
    </div>
  );
};

export default PmHistoryPage;

